import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import {
  EyeIcon,
  EyeSlashIcon,
  UserIcon,
  BuildingStorefrontIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

import {
  getCountries,
  getCountryCallingCode,
  AsYouType,
} from "libphonenumber-js";
import * as FlagIcons from "country-flag-icons/react/3x2";

import LocationSelector from "../../components/ui/LocationSelector";

const ALL_COUNTRIES = getCountries()
  .map((iso) => ({
    iso,
    code: `+${getCountryCallingCode(iso)}`,
    name: new Intl.DisplayNames(["en"], { type: "region" }).of(iso),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const RegisterPage = () => {
  const [userType, setUserType] = useState("pet_owner");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "+998",
    password: "",
    confirmPassword: "",
    country: null,
    state: null,
    city: null,
    agreeToTerms: false,
    companyName: "",
    businessType: "",
    address: "",
    description: "",
    taxId: "",
    website: "",
    referralSource: "",
    referralComment: "",
  });

  const [selectedCountry, setSelectedCountry] = useState({
    iso: "UZ",
    code: "+998",
    name: "Uzbekistan",
  });

  const [isPhoneDropdownOpen, setIsPhoneDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const phoneInputRef = useRef(null);

  const { register, registerBusiness } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  const fetchBusinessTypes = async () => {
    try {
      const response = await fetch("/api/v1/marketplace/business-types");

      if (response.ok) {
        const data = await response.json();
        setBusinessTypes(data.business_types || []);
      }
    } catch (error) {
      console.error("Failed to fetch business types:", error);
    }
  };

  const filteredCountries = useMemo(() => {
    return ALL_COUNTRIES.filter(
      (country) =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.code.includes(searchQuery),
    );
  }, [searchQuery]);

  const normalizeLocationValue = (value) => {
    if (!value) {
      return "";
    }

    if (typeof value === "string") {
      return value.trim();
    }

    return String(
      value.value || value.name || value.label || value.code || value.id || "",
    ).trim();
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    if (!value.startsWith("+")) {
      value = `+${value.replace(/[^\d]/g, "")}`;
    } else {
      value = `+${value.slice(1).replace(/[^\d]/g, "")}`;
    }

    const asYouType = new AsYouType();
    asYouType.input(value);

    const detected = asYouType.getCountry();

    if (detected && detected !== selectedCountry.iso) {
      const newCountry = ALL_COUNTRIES.find((country) => country.iso === detected);

      if (newCountry) {
        setSelectedCountry(newCountry);
      }
    }

    setFormData((prev) => ({
      ...prev,
      phone: value,
    }));

    if (errors.phone || errors.submit) {
      setErrors((prev) => ({
        ...prev,
        phone: "",
        submit: "",
      }));
    }
  };

  const FlagComponent = ({ iso, className }) => {
    const Flag = FlagIcons[iso];

    if (!Flag) {
      return <div className={`${className} bg-slate-200 rounded-sm`} />;
    }

    return <Flag className={className} />;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name] || errors.submit) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
        submit: "",
      }));
    }
  };

  const handleLocationChange = (location) => {
    const nextLocation = location || {};

    setFormData((prev) => ({
      ...prev,
      country: nextLocation.country ?? prev.country,
      state: nextLocation.state ?? prev.state,
      city: nextLocation.city ?? prev.city,
    }));

    if (errors.country || errors.city || errors.submit) {
      setErrors((prev) => ({
        ...prev,
        country: "",
        city: "",
        submit: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.phone.trim() || formData.phone.length < 8) {
      newErrors.phone = "Valid phone number is required";
    }

    if (!normalizeLocationValue(formData.country)) {
      newErrors.country = "Country is required";
    }

    if (!normalizeLocationValue(formData.city)) {
      newErrors.city = "City is required";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.referralSource) {
      newErrors.referralSource = "Please select how you heard about us";
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms";
    }

    if (userType === "business") {
      if (!formData.companyName.trim()) {
        newErrors.companyName = "Company name is required";
      }

      if (!formData.businessType) {
        newErrors.businessType = "Business type is required";
      }

      if (!formData.address.trim()) {
        newErrors.address = "Business address is required";
      }
    }

    console.log("REGISTER VALIDATION ERRORS:", newErrors);
    console.log("REGISTER FORM DATA:", formData);

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("REGISTER SUBMIT CLICKED");

    if (!validateForm()) {
      setErrors((prev) => ({
        ...prev,
        submit: "Please fill all required fields.",
      }));

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        country: normalizeLocationValue(formData.country),
        state: normalizeLocationValue(formData.state),
        city: normalizeLocationValue(formData.city),
      };

      console.log("REGISTER REQUEST PAYLOAD:", payload);

      if (userType === "business") {
        await registerBusiness({
          ...payload,
          userType: "business",
          role: "company_owner",
        });

        console.log("BUSINESS REGISTER SUCCESS");
        navigate("/company/");
        return;
      }

      await register({
        ...payload,
        role: "pet_owner",
      });

      console.log("PET OWNER REGISTER SUCCESS");
      navigate("/profile");
    } catch (error) {
      console.error("Registration failed:", error);

      setErrors({
        submit: error.message || "Registration failed. Please try again.",
      });

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle =
    "w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-[20px] text-sm text-slate-900 font-bold placeholder:text-slate-300 focus:bg-white focus:border-[#F65814] focus:ring-4 focus:ring-[#F65814]/5 transition-all duration-300 outline-none appearance-none";

  const labelStyle =
    "block text-[10px] font-black uppercase tracking-[2px] text-slate-400 mb-2 ml-2";

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center py-12 px-6 font-['Inter'] text-[#0A1128]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[600px]"
      >
        <div className="text-center mb-10">
          <h2 className="text-4xl font-[900] tracking-tight uppercase mb-2">
            Join <span className="text-[#F65814]">Zootel</span>
          </h2>

          <p className="text-slate-400 text-[13px] font-bold uppercase tracking-widest">
            Ready to start your journey?
          </p>
        </div>

        <div className="bg-slate-100 p-1.5 rounded-[26px] flex mb-8 relative">
          <motion.div
            layoutId="activeTab"
            className="absolute bg-white rounded-[22px] shadow-sm inset-y-1.5"
            style={{
              width: "calc(50% - 6px)",
              left: userType === "pet_owner" ? "6px" : "calc(50%)",
            }}
            transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
          />

          <button
            type="button"
            onClick={() => setUserType("pet_owner")}
            className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-[22px] text-[11px] font-black uppercase tracking-widest z-10 transition-colors ${
              userType === "pet_owner" ? "text-[#0A1128]" : "text-slate-400"
            }`}
          >
            <UserIcon className="h-4 w-4" />
            Pet Owner
          </button>

          <button
            type="button"
            onClick={() => setUserType("business")}
            className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-[22px] text-[11px] font-black uppercase tracking-widest z-10 transition-colors ${
              userType === "business" ? "text-[#0A1128]" : "text-slate-400"
            }`}
          >
            <BuildingStorefrontIcon className="h-4 w-4" />
            Business
          </button>
        </div>

        <div className="bg-white p-8 md:p-12 rounded-[48px] shadow-[0_32px_80px_-16px_rgba(0,0,0,0.08)] border border-slate-100">
          {errors.submit && (
            <div className="mb-6 rounded-[22px] border border-red-100 bg-red-50 p-4">
              <p className="text-[11px] font-black uppercase tracking-widest text-red-600">
                Registration failed
              </p>

              <p className="mt-2 text-xs font-bold text-red-500">
                {errors.submit}
              </p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <AnimatePresence mode="wait">
              <motion.div
                key={userType}
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -15 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                {userType === "business" && (
                  <div className="pb-6 border-b border-slate-50 space-y-4">
                    <h3 className="text-[11px] font-black uppercase tracking-[2.5px] text-[#F65814]">
                      Business Profile
                    </h3>

                    <div>
                      <label className={labelStyle}>Company Name</label>

                      <input
                        name="companyName"
                        type="text"
                        className={inputStyle}
                        placeholder="Zootel Pet Resort"
                        value={formData.companyName}
                        onChange={handleChange}
                      />

                      {errors.companyName && (
                        <p className="mt-2 text-xs font-bold text-red-500">
                          {errors.companyName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className={labelStyle}>Business Type</label>

                      <select
                        name="businessType"
                        className={inputStyle}
                        value={formData.businessType}
                        onChange={handleChange}
                      >
                        <option value="">Select business type</option>

                        {businessTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>

                      {errors.businessType && (
                        <p className="mt-2 text-xs font-bold text-red-500">
                          {errors.businessType}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className={labelStyle}>Business Address</label>

                      <input
                        name="address"
                        type="text"
                        className={inputStyle}
                        placeholder="Street address"
                        value={formData.address}
                        onChange={handleChange}
                      />

                      {errors.address && (
                        <p className="mt-2 text-xs font-bold text-red-500">
                          {errors.address}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className={labelStyle}>Description</label>

                      <textarea
                        name="description"
                        rows={3}
                        className={inputStyle}
                        placeholder="Brief description of your business"
                        value={formData.description}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelStyle}>First Name</label>

                    <input
                      name="firstName"
                      type="text"
                      className={inputStyle}
                      value={formData.firstName}
                      onChange={handleChange}
                    />

                    {errors.firstName && (
                      <p className="mt-2 text-xs font-bold text-red-500">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className={labelStyle}>Last Name</label>

                    <input
                      name="lastName"
                      type="text"
                      className={inputStyle}
                      value={formData.lastName}
                      onChange={handleChange}
                    />

                    {errors.lastName && (
                      <p className="mt-2 text-xs font-bold text-red-500">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className={labelStyle}>Email Address</label>

                  <input
                    name="email"
                    type="email"
                    className={inputStyle}
                    placeholder="hello@zootel.com"
                    value={formData.email}
                    onChange={handleChange}
                  />

                  {errors.email && (
                    <p className="mt-2 text-xs font-bold text-red-500">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="relative">
                  <label className={labelStyle}>Phone Number</label>

                  <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-[20px] px-5 focus-within:bg-white focus-within:border-[#F65814] focus-within:ring-4 focus-within:ring-[#F65814]/5 transition-all duration-300">
                    <button
                      type="button"
                      onClick={() => setIsPhoneDropdownOpen(!isPhoneDropdownOpen)}
                      className="flex items-center gap-2 py-4 outline-none group shrink-0"
                    >
                      <FlagComponent
                        iso={selectedCountry.iso}
                        className="w-6 h-4 object-cover rounded-sm shadow-sm"
                      />

                      <ChevronDownIcon className="h-3 w-3 text-slate-400 group-hover:text-[#F65814] transition-colors" />
                    </button>

                    <input
                      ref={phoneInputRef}
                      type="tel"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className="w-full bg-transparent ml-4 py-4 text-sm font-bold text-slate-900 outline-none border-none focus:ring-0"
                      placeholder="+7 000 000 00 00"
                    />

                    <AnimatePresence>
                      {isPhoneDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-[60]"
                            onClick={() => setIsPhoneDropdownOpen(false)}
                          />

                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="absolute top-[calc(100%+10px)] left-0 w-full max-w-[320px] bg-white border border-slate-100 rounded-[28px] shadow-[0_30px_70px_rgba(0,0,0,0.18)] z-[70] overflow-hidden p-2"
                          >
                            <div className="p-3 mb-1 border-b border-slate-50 flex items-center gap-2">
                              <MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />

                              <input
                                autoFocus
                                placeholder="Find country..."
                                className="w-full text-xs font-bold outline-none"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                              />
                            </div>

                            <div className="max-h-[240px] overflow-y-auto">
                              {filteredCountries.map((country) => (
                                <button
                                  key={country.iso}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCountry(country);
                                    setFormData((prev) => ({
                                      ...prev,
                                      phone: country.code,
                                    }));
                                    setIsPhoneDropdownOpen(false);
                                    phoneInputRef.current?.focus();
                                  }}
                                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 rounded-[18px] transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <FlagComponent
                                      iso={country.iso}
                                      className="w-6 h-4 rounded-sm object-cover shadow-sm"
                                    />

                                    <span className="text-[11px] font-black text-slate-800 uppercase tracking-tight">
                                      {country.name}
                                    </span>
                                  </div>

                                  <span className="text-[10px] font-bold text-slate-400">
                                    {country.code}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>

                  {errors.phone && (
                    <p className="mt-2 text-xs font-bold text-red-500">
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <LocationSelector
                    selectedCountry={formData.country}
                    selectedState={formData.state}
                    selectedCity={formData.city}
                    onLocationChange={handleLocationChange}
                    errors={errors}
                  />

                  {(errors.country || errors.city) && (
                    <p className="mt-2 text-xs font-bold text-red-500">
                      {errors.country || errors.city}
                    </p>
                  )}
                </div>

                <div className="space-y-4 rounded-[28px] border border-slate-100 bg-slate-50/60 p-5">
                  <div>
                    <label className={labelStyle}>How did you hear about us?</label>

                    <select
                      name="referralSource"
                      className={inputStyle}
                      value={formData.referralSource}
                      onChange={handleChange}
                    >
                      <option value="">Select source</option>
                      <option value="social_media">Social media</option>
                      <option value="google_search">Google / Search</option>
                      <option value="friend_recommendation">Friend recommendation</option>
                      <option value="ads">Ads</option>
                      <option value="partner">Partner</option>
                      <option value="event">Event</option>
                      <option value="other">Other</option>
                    </select>

                    {errors.referralSource && (
                      <p className="mt-2 text-xs font-bold text-red-500">
                        {errors.referralSource}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className={labelStyle}>Comment</label>

                    <textarea
                      name="referralComment"
                      rows={3}
                      className={inputStyle}
                      placeholder="Optional comment..."
                      value={formData.referralComment}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <label className={labelStyle}>Password</label>

                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      className={`${inputStyle} pr-12`}
                      value={formData.password}
                      onChange={handleChange}
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 text-slate-300 hover:text-[#F65814] transition-colors"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  {errors.password && (
                    <p className="mt-2 text-xs font-bold text-red-500">
                      {errors.password}
                    </p>
                  )}
                </div>

                  <div>
                    <label className={labelStyle}>Confirm</label>

                    <input
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      className={inputStyle}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />

                    {errors.confirmPassword && (
                      <p className="mt-2 text-xs font-bold text-red-500">
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="space-y-6 pt-6">
              <div className="flex items-start gap-3 px-2">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 rounded border-slate-200 text-[#F65814] focus:ring-[#F65814]/10 cursor-pointer"
                />

                <label className="text-[11px] font-bold text-slate-400 leading-snug">
                  I agree to the{" "}
                  <Link to="/terms" className="text-[#0A1128] font-black">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-[#0A1128] font-black">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              {errors.agreeToTerms && (
                <p className="px-2 text-xs font-bold text-red-500">
                  {errors.agreeToTerms}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0A1128] text-white py-5 rounded-[26px] font-[900] uppercase tracking-[3px] text-[14px] flex items-center justify-center gap-3 hover:bg-[#F65814] transition-all duration-500 shadow-[0_20px_40px_rgba(10,17,40,0.2)] hover:shadow-[#F65814]/25 group disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading
                  ? "Creating..."
                  : userType === "business"
                    ? "Launch Business"
                    : "Create Account"}

                <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
              </button>
            </div>
          </form>
        </div>

        <p className="text-center mt-12 text-slate-400 text-[11px] font-black uppercase tracking-[2.5px]">
          Already joined?{" "}
          <Link
            to="/login"
            className="text-[#F65814] border-b-2 border-transparent hover:border-[#F65814] transition-all ml-1"
          >
            Sign In
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default RegisterPage;